import { ArrowDown, ArrowUp, Clock3, GripVertical, ListMusic, Play, Sparkles, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { usePlayer } from "../contexts/PlayerContext";
import "../styles/views/queue-view.css";

function formatDuration(ms) {
  const parsed = Number(ms);
  if (!Number.isFinite(parsed) || parsed <= 0) return "--:--";
  const totalSeconds = Math.floor(parsed / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function QueueView() {
  const [dragIndex, setDragIndex] = useState(-1);
  const [dropIndex, setDropIndex] = useState(-1);

  const {
    queue,
    currentIndex,
    currentTrack,
    isPlaying,
    playQueueIndex,
    removeQueueIndex,
    moveQueueIndex,
    clearQueue,
  } = usePlayer();

  const stats = useMemo(() => {
    const totalTracks = queue.length;
    const totalDurationMs = queue.reduce((sum, item) => sum + (Number(item?.durationMs) || 0), 0);
    return {
      totalTracks,
      totalDuration: formatDuration(totalDurationMs),
      upNextCount: Math.max(0, totalTracks - (totalTracks ? 1 : 0)),
    };
  }, [queue]);

  const upNextItems = queue
    .map((item, index) => ({ item, index }))
    .filter((entry) => entry.index !== currentIndex);

  function handleDragStart(index) {
    setDragIndex(index);
    setDropIndex(index);
  }

  function handleDragEnter(index) {
    if (index === dragIndex) return;
    setDropIndex(index);
  }

  function handleDragEnd() {
    setDragIndex(-1);
    setDropIndex(-1);
  }

  function handleDrop(index) {
    if (dragIndex < 0 || index < 0 || dragIndex === index) {
      handleDragEnd();
      return;
    }

    moveQueueIndex(dragIndex, index);
    handleDragEnd();
  }

  if (!queue.length) {
    return (
      <section className="glass-panel panel-block queue-empty-state">
        <div className="queue-empty-icon">
          <ListMusic size={22} />
        </div>
        <h3 className="section-title">Queue is empty</h3>
        <p>Add tracks from Home, Search, or Related to start a listening session.</p>
      </section>
    );
  }

  return (
    <>
      <section className="glass-panel panel-block queue-hero">
        <div className="queue-hero-glow queue-hero-glow-a" />
        <div className="queue-hero-glow queue-hero-glow-b" />
        <div className="queue-hero-head">
          <div>
            <p className="queue-kicker">Live Playback Queue</p>
            <h3 className="queue-title">Queue Control Center</h3>
            <p className="queue-subtitle">Reorder upcoming tracks and keep your listening flow clean.</p>
          </div>

          <div className="queue-stats">
            <span>
              <Sparkles size={14} />
              Up next {stats.upNextCount}
            </span>
            <span>
              <Clock3 size={14} />
              {stats.totalDuration}
            </span>
            <span>
              <ListMusic size={14} />
              {stats.totalTracks} tracks
            </span>
          </div>
        </div>

        <article className="queue-now-playing glass-panel">
          <img src={currentTrack?.image} alt={currentTrack?.title || "Now playing"} />
          <div className="queue-now-playing-content">
            <p className="queue-now-playing-title">{currentTrack?.title || "No track selected"}</p>
            <p className="queue-now-playing-artist">
              {currentTrack?.isUnavailable
                ? currentTrack?.unavailableText || "Not available in your country"
                : currentTrack?.artist || "Unknown artist"}
            </p>
          </div>
          <span className={`queue-status-badge${isPlaying ? " is-active" : ""}`}>
            {isPlaying ? "Playing" : "Paused"}
          </span>
        </article>

        <div className="queue-actions">
          <button
            type="button"
            className="section-link-btn"
            onClick={clearQueue}
            aria-label="Clear queue"
          >
            <Trash2 size={14} />
            Clear queue
          </button>
        </div>
      </section>

      <section className="glass-panel panel-block">
        <div className="section-inline">
          <h3 className="section-title">Up Next</h3>
          <p className="section-subtle inline-icon">
            <Clock3 size={14} />
            {upNextItems.length} upcoming
          </p>
        </div>

        {upNextItems.length ? (
          <div className="queue-list-view">
            {upNextItems.map(({ item, index: actualIndex }) => {
              return (
                <article
                  key={`${item.id}-${actualIndex}`}
                  className={`queue-view-item${item.isUnavailable ? " is-unavailable" : ""}${dragIndex === actualIndex ? " is-dragging" : ""}${dropIndex === actualIndex && dragIndex !== actualIndex ? " is-drop-target" : ""}`}
                  onDragEnter={() => handleDragEnter(actualIndex)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => handleDrop(actualIndex)}
                >
                  <button
                    type="button"
                    className="queue-view-main"
                    onClick={() => playQueueIndex(actualIndex)}
                    disabled={item.isUnavailable}
                  >
                    <img src={item.image} alt={item.title} />
                    <div className="queue-view-main-text">
                      <p className="queue-view-title">{item.title}</p>
                      <p className={`queue-view-artist${item.isUnavailable ? " is-warning" : ""}`}>
                        {item.isUnavailable
                          ? item.unavailableText || "Not available in your country"
                          : item.artist}
                      </p>
                    </div>
                    <span className="queue-view-duration">{formatDuration(item.durationMs)}</span>
                  </button>

                  <div className="queue-view-item-actions">
                    <button
                      type="button"
                      className="queue-icon-btn queue-drag-btn"
                      aria-label={`Drag to reorder ${item.title}`}
                      draggable
                      onDragStart={() => handleDragStart(actualIndex)}
                      onDragEnd={handleDragEnd}
                    >
                      <GripVertical size={14} />
                    </button>
                    <button
                      type="button"
                      className="queue-icon-btn"
                      aria-label={`Move ${item.title} up`}
                      onClick={() => moveQueueIndex(actualIndex, actualIndex - 1)}
                      disabled={actualIndex <= 0}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      className="queue-icon-btn"
                      aria-label={`Move ${item.title} down`}
                      onClick={() => moveQueueIndex(actualIndex, actualIndex + 1)}
                      disabled={actualIndex >= queue.length - 1}
                    >
                      <ArrowDown size={14} />
                    </button>
                    <button
                      type="button"
                      className="queue-icon-btn"
                      aria-label={`Play ${item.title}`}
                      disabled={item.isUnavailable}
                      onClick={() => playQueueIndex(actualIndex)}
                    >
                      <Play size={14} />
                    </button>
                    <button
                      type="button"
                      className="queue-icon-btn"
                      aria-label={`Remove ${item.title} from queue`}
                      onClick={() => removeQueueIndex(actualIndex)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="home-state-inline">No upcoming tracks in queue.</p>
        )}
      </section>
    </>
  );
}

export default QueueView;
